/**
 * Pantalla /reset-password — destino del enlace que envía el mail.service.
 *
 * Lee el `oobCode` del query string, lo valida con Firebase
 * (verifyPasswordResetCode) y permite definir la contraseña nueva
 * (confirmPasswordReset). No requiere sesión: el Router la renderiza antes
 * del guard de autenticación.
 */

import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Result, Spin, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useFirebase } from 'react-redux-firebase';
import '../styles/login.scss';

const FormItem = Form.Item;
const LogoCompany = 'https://firebasestorage.googleapis.com/v0/b/gestor-documental-67434.appspot.com/o/Cabezote_Web_CR.gif?alt=media&token=71d4c061-1cd3-4ed7-b23c-ee25f5c2c916';

export const ResetPassword = () => {

  const firebase = useFirebase();
  // Hook para el control de los formularios
  const [form] = Form.useForm();

  // checking → validando el enlace | ready → formulario | invalid → enlace
  // vencido/usado/ausente | done → contraseña cambiada
  const [status, setStatus] = useState('checking');
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const oobCode = new URLSearchParams(window.location.search).get('oobCode');

  useEffect(() => {
    if (!oobCode) {
      setStatus('invalid');
      return;
    }
    firebase.auth().verifyPasswordResetCode(oobCode)
      .then( accountEmail => {
        setEmail(accountEmail);
        setStatus('ready');
      })
      .catch( () => setStatus('invalid') );
  }, [firebase, oobCode]);

  const onFinish = ({ password }) => {
    setLoading(true);
    setError(null);
    firebase.auth().confirmPasswordReset(oobCode, password)
      .then( () => setStatus('done') )
      .catch( err => {
        if (err?.code === 'auth/expired-action-code' || err?.code === 'auth/invalid-action-code') {
          setStatus('invalid'); // vencido o ya usado entre la validación y el submit
        } else if (err?.code === 'auth/weak-password') {
          setError('La contraseña es muy débil. Use al menos 6 caracteres.');
        } else {
          setError('No se pudo cambiar la contraseña. Intente de nuevo.');
        }
      })
      .finally( () => setLoading(false) );
  };

  const goToLogin = () => { window.location.href = '/'; };
  // ?forgot=1 → el Login abre el modal de recuperación directo (ver login/index.js)
  const requestNewLink = () => { window.location.href = '/?forgot=1'; };

  const content = () => {

    if (status === 'checking') return <Spin size = "large" tip = "Validando el enlace..." />;

    if (status === 'invalid') return (
      <Result
        status = "warning"
        title = "El enlace no es válido"
        subTitle = "El enlace expiró o ya fue usado. Solicita uno nuevo para restablecer tu contraseña."
        extra = {[
          <Button type = "primary" key = "new" onClick = { requestNewLink } > Solicitar un enlace nuevo </Button>,
          <Button key = "login" onClick = { goToLogin } > Volver a iniciar sesión </Button>
        ]} />
    );

    if (status === 'done') return (
      <Result
        status = "success"
        title = "Contraseña actualizada"
        subTitle = "Ya puedes iniciar sesión con tu contraseña nueva."
        extra = { <Button type = "primary" onClick = { goToLogin } > Ir a iniciar sesión </Button> } />
    );

    // status === 'ready'
    return (
      <Form form = { form } className = "login-form" layout = "vertical" onFinish = { onFinish } >
        <Typography.Title level = { 4 } > Restablecer contraseña </Typography.Title>
        <p> Define la contraseña nueva para <strong>{ email }</strong> </p>
        <FormItem name = "password" label = "Nueva contraseña" hasFeedback
          rules = {[
            { required: true, message: 'Debe ingresar la contraseña nueva' },
            { min: 6, message: 'Debe tener mínimo 6 caracteres' }
          ]} >
          <Input.Password placeholder = "Mínimo 6 caracteres" prefix = { <LockOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
        </FormItem>
        <FormItem name = "confirm" label = "Confirmar contraseña" dependencies = {[ 'password' ]} hasFeedback
          rules = {[
            { required: true, message: 'Debe confirmar la contraseña' },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('password') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Las contraseñas no coinciden'))
            })
          ]} >
          <Input.Password placeholder = "Repite la contraseña" prefix = { <LockOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
        </FormItem>
        { error && <p style = {{ color: '#E70810' }} > { error } </p> }
        <FormItem>
          <Button type = "primary" htmlType = "submit" className = "login-form-button" loading = { loading } > Cambiar contraseña </Button>
        </FormItem>
      </Form>
    );
  };

  return (
    <div className = "cuerpo cuerpo-mfa" >
      <div className = "imagenInicio" >
        <img alt = "logo" src = { LogoCompany } />
      </div>
      <div className = "login login-mfa" >
        { content() }
      </div>
    </div>
  );

};
